import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
	json,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import {
	Form,
	Link,
	useActionData,
	useSearchParams,
	type MetaFunction,
} from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { CheckboxField, ErrorList, Field } from '#app/components/forms.tsx'
import { Separator } from '#app/components/ui/separator.js'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { redirectWithToast } from '#app/utils/toast.server.js'
import { PasswordSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import { handleNewSession } from './login.server.ts'

export const meta: MetaFunction = ({ data }) => {
	return [{ title: 'Kogia | Login' }]
}

const LoginFormSchema = z.object({
	username: UsernameSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	try {
		await requireAnonymous(request)
	} catch (error) {
		return redirectWithToast('/', {
			type: 'error',
			title: 'You are logged in already',
			description: "You can't log in when you are already logged in",
		})
	}
	return json({})
}

export async function action({ request }: ActionFunctionArgs) {
	try {
		await requireAnonymous(request)
	} catch (error) {
		return redirectWithToast('/', {
			type: 'error',
			title: 'You are logged in already',
			description: "You can't log in when you are already logged in",
		})
	}
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: intent =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Invalid username or password',
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})
	if (submission.status !== 'success' || !submission.value.session) {
		return json(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { session, remember, redirectTo } = submission.value
	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LoginPage() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})
	return (
		<div className="flex w-full max-w-screen-md flex-col gap-3 p-4 md:p-8">
			<div className="flex items-center gap-4">
				<h2 className="font-serif text-h2">Welcome back!</h2>
				<Separator className="flex-1" />
			</div>
			<p className="text-body-md text-muted-foreground">
				Please enter your details.
			</p>
			<Form method="POST" {...getFormProps(form)}>
				<HoneypotInputs />
				<Field
					labelProps={{ children: 'Username' }}
					inputProps={{
						...getInputProps(fields.username, { type: 'text' }),
						autoFocus: true,
						className: 'lowercase',
						autoComplete: 'username',
					}}
					errors={fields.username.errors}
				/>

				<Field
					labelProps={{ children: 'Password' }}
					inputProps={{
						...getInputProps(fields.password, {
							type: 'password',
						}),
						autoComplete: 'current-password',
					}}
					errors={fields.password.errors}
				/>

				<div className="flex items-start justify-between">
					<CheckboxField
						labelProps={{
							htmlFor: fields.remember.id,
							children: 'Remember me',
						}}
						buttonProps={getInputProps(fields.remember, {
							type: 'checkbox',
						})}
						errors={fields.remember.errors}
					/>
					<Link to="/forgot-password" className="text-body-xs font-semibold">
						Forgot password?
					</Link>
				</div>
				<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
				<ErrorList errors={form.errors} id={form.errorId} />
				<div className="flex items-center justify-between gap-6 pt-3">
					<StatusButton
						className="w-full"
						status={isPending ? 'pending' : form.status ?? 'idle'}
						type="submit"
						disabled={isPending}
					>
						Log in
					</StatusButton>
				</div>
			</Form>
			<div className="flex items-center justify-center gap-2 pt-6">
				<span className="text-muted-foreground">New here?</span>
				<Link className="font-bold" to={'/apply'}>
					Request Access
				</Link>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
